import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { DotsVerticalIcon } from "@heroicons/react/outline";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

// `label` names the menu button for screen readers, e.g. "Actions for
// INV-001"; without it the button reads as the generic "Actions".
// An action with `disabled` stays visible but cannot be triggered; its
// `tooltip` explains why (e.g. a record already used in transactions).
const TableActions = ({ actions = [], position = "bottom", label }) => {
  const { t } = useTranslation();

  if (actions.length === 0) return null;

  if (actions.length === 1) {
    const action = actions[0];
    return (
      <button
        onClick={action.disabled ? undefined : action.onClick}
        aria-disabled={action.disabled || undefined}
        title={action.tooltip}
        className={`inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
          action.disabled
            ? "opacity-50 cursor-not-allowed"
            : action.variant === "danger"
            ? "hover:bg-gray-50 hover:text-red-600 hover:border-red-600"
            : "hover:bg-gray-50"
        }`}
      >
        {action.label}
      </button>
    );
  }

  return (
    <Menu as="div" className="relative inline-block text-left">
      <MenuButton
        aria-label={label || t("common.actions.label")}
        className="p-1 rounded-full hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
      >
        <DotsVerticalIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
      </MenuButton>

      <MenuItems
        transition
        anchor={position === "top" ? "top end" : "bottom end"}
        className={clsx(
          position === "top" ? "origin-bottom-right" : "origin-top-right",
          "w-48 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 [--anchor-gap:4px] sm:[--anchor-gap:8px] focus:outline-none transition duration-100 ease-out data-[closed]:scale-95 data-[closed]:opacity-0"
        )}
      >
        <div className="py-1">
          {actions.map((action, index) => (
            <MenuItem key={index} disabled={action.disabled}>
              {({ focus }) => (
                <button
                  onClick={action.disabled ? undefined : action.onClick}
                  title={action.tooltip}
                  className={clsx(
                    "block data-[focus]:bg-gray-100 px-4 py-2 text-sm w-full text-left",
                    action.variant === "danger"
                      ? "text-red-600"
                      : "text-gray-700",
                    action.disabled && "opacity-50 cursor-not-allowed"
                  )}
                >
                  {action.label}
                </button>
              )}
            </MenuItem>
          ))}
        </div>
      </MenuItems>
    </Menu>
  );
};

export default TableActions;
